import type { ReactNode } from 'react';
import { hideVisually } from 'polished';
import styled from '@emotion/styled';
import ArrowDown from '@engineering/icons/arrow-down';
import { css } from '@emotion/react';

type DropdownProps = {
  disabled?: boolean;
  icon?: ReactNode;
  isCompact?: boolean;
  $isInvalid?: boolean;
};

const DropdownWrapper = styled.span`
  position: relative;
  display: flex;
  justify-content: center;
  column-gap: 0.25rem;
`;

const Label = styled.label<DropdownProps>`
  ${({ theme, isCompact, $isInvalid }) =>
    isCompact
      ? hideVisually()
      : css`
          position: absolute;
          top: -0.5625rem;
          left: 0.75rem;
          z-index: 2;
          margin: 0;
          padding: 0 0.25rem;
          color: ${$isInvalid
            ? theme.colors.accent.red100
            : theme.colors.neutral.grey140};
          background-color: ${theme.colors.neutral.white100};
          font-size: 0.75rem;
          line-height: 1.5;
          overflow: hidden;
          pointer-events: none;
          text-overflow: ellipsis;
          white-space: nowrap;

          &:empty ~ select {
            padding-top: 0.1875rem;
            padding-bottom: 0.1875rem;
          }
        `}
`;

const Select = styled.select<DropdownProps>`
  position: relative;
  margin: 0;
  padding: ${({ theme, isCompact, icon }) =>
    isCompact
      ? `${theme.spacing[10]} ${theme.spacing[50]} ${theme.spacing[10]} ${icon ? theme.spacing[60] : theme.spacing[10]}`
      : `0.625rem ${theme.spacing[80]} 0.625rem ${icon ? theme.spacing[70] : theme.spacing[40]}`};
  border: ${({ theme, isCompact, disabled, $isInvalid }) => {
    if (isCompact) {
      return 0;
    }
    if ($isInvalid) {
      return `1px solid ${theme.colors.accent.red100}`;
    }
    return `1px solid ${disabled ? theme.colors.neutral.grey40 : theme.colors.neutral.grey80}`;
  }};
  border-radius: ${({ theme }) => theme.radius.sm};
  outline: 0;
  width: 100%;
  height: ${({ isCompact }) => (isCompact ? '2rem' : '3.125rem')};
  font-size: 1rem;
  font-family: inherit;
  line-height: 1.5;
  color: ${({ theme, disabled }) =>
    disabled ? theme.colors.neutral.grey120 : theme.colors.neutral.grey160};
  background-color: ${({ theme }) => theme.colors.neutral.white100};
  appearance: none;
  cursor: pointer;

  ${({ theme, $isInvalid, isCompact }) =>
    !$isInvalid &&
    css`
      &:hover {
        &:not([disabled]) {
          border-color: ${theme.colors.neutral.grey140};
          background-color: ${isCompact ? theme.colors.neutral.grey40 : ''};
        }
      }
    `}

  &:focus-visible {
    outline: 0.125rem dashed ${({ theme }) => theme.colors.brand.orange100};
    outline-offset: 0.25rem;
  }
`;

const ArrowIconWrapper = styled.span<DropdownProps & { isDisabled: boolean }>`
  position: absolute;
  top: ${({ isCompact }) => (isCompact ? '0.5rem' : '0.875rem')};
  right: ${({ isCompact }) => (isCompact ? '0.25rem' : '0.9375rem')};
  fill: ${({ theme, isDisabled }) =>
    isDisabled ? theme.colors.neutral.grey120 : theme.colors.neutral.grey160};
  width: ${({ isCompact }) => (isCompact ? '0.75rem' : '1.5rem')};
  height: ${({ isCompact }) => (isCompact ? '0.75rem' : '1.5rem')};
  pointer-events: none;
`;

const ArrowIcon = styled(ArrowDown)`
  width: 100%;
  height: 100%;
`;

const IconWrapper = styled.span<DropdownProps & { isDisabled: boolean }>`
  position: absolute;
  left: ${({ isCompact }) => (isCompact ? '0.5rem' : '0.75rem')};
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: start;
  width: ${({ isCompact }) => (isCompact ? '1rem' : '1.5rem')};
  height: ${({ isCompact }) => (isCompact ? '1rem' : '1.5rem')};
  overflow: hidden;
  color: ${({ theme, isDisabled }) =>
    isDisabled ? theme.colors.neutral.grey120 : theme.colors.neutral.grey160};
  fill: currentcolor;
  pointer-events: none;
`;

const Hint = styled.p`
  margin: ${({ theme }) => `${theme.spacing[5]} ${theme.spacing[40]} 0`};
  color: ${({ theme }) => theme.colors.neutral.grey120};
  font-size: ${({ theme }) => theme.fontSize.xxs.size};
`;

export {
  DropdownWrapper,
  Label,
  Select,
  ArrowIcon,
  ArrowIconWrapper,
  IconWrapper,
  Hint,
};
